/*
 * BindingField — ein Eigenschaftsfeld des HMI-Editors, das an einen
 * Datenpunkt bindet.
 *
 * Das Feld traegt nach aussen einen String (den Binding-Key aus
 * `hmi/bindingKey.ts`), nach innen aber muss es etwas anderes zeigen: einen
 * Guid im Eigenschaftsfeld kann niemand lesen, und ein HMI-Bild wird von
 * Menschen gebaut, die die Anlage kennen, nicht die Ids. Deshalb loest das
 * Feld den Key gegen das Anlagenschema auf und zeigt Anzeigename und Pfad.
 *
 * Drei Zustaende sind es wert, unterschieden zu werden:
 *
 *   1. Nicht gebunden. Bei `required` sichtbar als Mangel, nicht als leeres
 *      Feld. Ein leeres Pflichtfeld, das aussieht wie ein normales leeres
 *      Feld, faellt erst auf der Anlage auf, wo das Bild dann nichts anzeigt.
 *   2. Gebunden und auffindbar. Name, Pfad, Einheit, Werttyp.
 *   3. Gebunden und NICHT auffindbar. Das ist kein theoretischer Fall:
 *      Projekte werden kopiert und Vorlagen uebertragen, und dann zeigt ein
 *      Key auf eine fremde Anlage oder auf einen Punkt, den es hier nicht
 *      gibt. Still den Guid anzeigen waere die schlechteste Antwort; das Feld
 *      sagt stattdessen, was los ist, und unterscheidet dabei "gehoert zu
 *      einer anderen Anlage" von "in dieser Anlage nicht gefunden" — das ist
 *      der Unterschied zwischen einem Kopierfehler und einem
 *      Schema-Unterschied.
 *
 * Zusaetzlich meldet das Feld, wenn ein gebundener Punkt nicht zu seinem
 * eigenen Filter passt (ein Schieberegler an einem Bool-Punkt). Der Waehler
 * verhindert das beim Waehlen; hier faellt es auf, wenn ein Key aus einer
 * Vorlage kommt oder das Schema sich geaendert hat.
 *
 * Der Vertrag (`BindingFieldProps`) ist festgelegt, weil die
 * Eigenschaftseditoren parallel dagegen gebaut werden. Nicht erweitern, ohne
 * das abzustimmen.
 */

import { useMemo, useState } from 'preact/hooks';
import type { VNode } from 'preact';
import { DatapointPicker, usePlantDatapoints } from '../../../components/DatapointPicker';
import {
  VALUE_TYPE_LABELS,
  classifyValueType,
  isWritableLeaf,
  type DatapointRow,
} from '../../../components/datapointRows';
import { isDemoBindingKey, parseBindingKey } from '../../bindingKey';
import './BindingField.css';

export interface BindingFieldProps {
  label: string;
  /** Aktueller Binding-Key, oder undefined wenn nicht gebunden. */
  value: string | undefined;
  /** Anlage, in der gesucht wird. */
  plantId: string;
  /** Pflichtfeld: wird sichtbar als fehlend markiert, wenn leer. */
  required?: boolean;
  /** Einschraenkung der waehlbaren Punkte. */
  filter?: { writable?: boolean; valueType?: 'number' | 'bool' | 'enum' | 'string' };
  /** Kurzer Hinweis unter dem Feld, etwa "Der Wert, den die Anzeige zeigt". */
  hint?: string;
  onChange(next: string | undefined): void;
}

/** Wie der Key zu dieser Anlage steht. */
type Resolution =
  | { kind: 'empty' }
  /** Kein auflösbarer Live-Key (Demo-Namensraum oder kaputt). */
  | { kind: 'malformed'; demo: boolean }
  /** Gueltiger Key, aber auf eine andere Anlage. */
  | { kind: 'foreign-plant'; plantId: string }
  /** Richtige Anlage, Punkt aber nicht im Schema. */
  | { kind: 'missing' }
  | { kind: 'found'; row: DatapointRow };

export function BindingField(props: BindingFieldProps): VNode {
  const { label, value, plantId, required, filter, hint, onChange } = props;
  const [pickerOpen, setPickerOpen] = useState(false);
  const state = usePlantDatapoints(plantId);
  const index = state.status === 'ok' ? state.data.byLeafId : null;

  const resolution = useMemo<Resolution>(
    () => resolve(value, plantId, index),
    [value, plantId, index],
  );

  // Der Filter wird auf den bereits gebundenen Punkt nachtraeglich angewandt.
  // Kein Fehler, sondern eine Warnung: den Key hier von selbst zu loeschen
  // waere Datenverlust an einer Stelle, an der der Autor nichts getan hat.
  const mismatch = resolution.kind === 'found' ? describeMismatch(resolution.row, filter) : null;

  const missingRequired = required === true && resolution.kind === 'empty';
  const broken = resolution.kind === 'malformed'
    || resolution.kind === 'foreign-plant'
    || resolution.kind === 'missing';

  const tone = broken || missingRequired ? ' hv-binding-field--bad'
    : mismatch ? ' hv-binding-field--warn'
    : '';

  return (
    <div class={`hv-binding-field${tone}`}>
      <div class="hv-binding-field__label">
        <span>{label}</span>
        {required && <span class="hv-binding-field__req" title="Pflichtfeld">*</span>}
      </div>

      <div class="hv-binding-field__box">
        <button
          type="button"
          class="hv-binding-field__value"
          onClick={() => setPickerOpen(true)}
          title={value ?? 'Noch kein Datenpunkt gebunden'}
        >
          {renderValue(resolution, state.status === 'loading', missingRequired)}
        </button>

        <div class="hv-binding-field__actions">
          <button
            type="button"
            class="hv-binding-field__btn"
            onClick={() => setPickerOpen(true)}
          >
            {resolution.kind === 'empty' ? 'Binden' : 'Ändern'}
          </button>
          {resolution.kind !== 'empty' && (
            <button
              type="button"
              class="hv-binding-field__btn hv-binding-field__btn--clear"
              onClick={() => onChange(undefined)}
              title="Bindung lösen"
            >
              Lösen
            </button>
          )}
        </div>
      </div>

      {mismatch && <p class="hv-binding-field__warn">{mismatch}</p>}
      {/* Bei kaputter Bindung steht der rohe Key dabei: er ist die einzige
          Spur, die zur Herkunft des Projekts zurueckfuehrt. Aber als Beleg
          unter dem Klartext, nicht anstelle davon. */}
      {broken && value && <p class="hv-binding-field__raw mono">{value}</p>}
      {hint && <p class="hv-binding-field__hint">{hint}</p>}

      {pickerOpen && (
        <DatapointPicker
          plantId={plantId}
          value={value}
          filter={filter}
          title={label}
          onPick={(next) => onChange(next)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function resolve(
  value: string | undefined,
  plantId: string,
  index: Map<string, DatapointRow> | null,
): Resolution {
  if (value === undefined || value.trim().length === 0) return { kind: 'empty' };
  const parsed = parseBindingKey(value);
  if (!parsed) return { kind: 'malformed', demo: isDemoBindingKey(value) };
  if (parsed.plantId !== plantId.trim().toLowerCase()) {
    return { kind: 'foreign-plant', plantId: parsed.plantId };
  }
  // Solange das Schema laedt, ist "nicht gefunden" noch keine Aussage —
  // deshalb erst mit Index urteilen.
  if (!index) return { kind: 'missing' };
  const row = index.get(parsed.leafId);
  return row ? { kind: 'found', row } : { kind: 'missing' };
}

function renderValue(resolution: Resolution, loading: boolean, missingRequired: boolean): VNode {
  switch (resolution.kind) {
    case 'empty':
      return (
        <span class={`hv-binding-field__empty${missingRequired ? ' hv-binding-field__empty--req' : ''}`}>
          {missingRequired ? 'Pflichtfeld: nicht gebunden' : 'Nicht gebunden'}
        </span>
      );
    case 'malformed':
      return (
        <span class="hv-binding-field__bad">
          {resolution.demo
            ? 'Demo-Datenpunkt: zeigt auf der Anlage nichts an'
            : 'Unlesbare Bindung'}
        </span>
      );
    case 'foreign-plant':
      return (
        <span class="hv-binding-field__bad">
          Datenpunkt einer anderen Anlage
        </span>
      );
    case 'missing':
      return (
        <span class={loading ? 'hv-binding-field__loading' : 'hv-binding-field__bad'}>
          {loading ? 'Datenpunkt wird aufgelöst…' : 'Datenpunkt nicht in dieser Anlage'}
        </span>
      );
    case 'found': {
      const leaf = resolution.row.leaf;
      const kind = classifyValueType(leaf);
      return (
        <span class="hv-binding-field__found">
          <span class="hv-binding-field__name">{leaf.displayName || leaf.symbol}</span>
          <span class="hv-binding-field__meta">
            <span class="hv-binding-field__badge">{kind ? VALUE_TYPE_LABELS[kind] : leaf.valueType}</span>
            {leaf.unit && <span class="hv-binding-field__unit">{leaf.unit}</span>}
          </span>
          <span class="hv-binding-field__path">
            {resolution.row.deviceSymbol} › {resolution.row.path} › {leaf.symbol}
          </span>
        </span>
      );
    }
  }
}

/** Passt der gebundene Punkt noch zu dem, was dieses Feld verlangt? */
function describeMismatch(row: DatapointRow, filter: BindingFieldProps['filter']): string | null {
  if (!filter) return null;
  if (filter.writable === true && !isWritableLeaf(row.leaf)) {
    return 'Dieser Datenpunkt ist nur lesbar, das Widget will aber schreiben.';
  }
  if (filter.valueType !== undefined && classifyValueType(row.leaf) !== filter.valueType) {
    return `Werttyp passt nicht: gebunden ist ${row.leaf.valueType}, erwartet wird `
      + `${VALUE_TYPE_LABELS[filter.valueType]}.`;
  }
  return null;
}
